'use client';

import Link from 'next/link';
import {
  Apple,
  Chrome,
  Clock,
  Download,
  ExternalLink,
  Monitor,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { usePwaInstall, type PwaPlatform } from '@/components/use-pwa-install';
import { cn } from '@/lib/utils';

/**
 * Embedded `/local` view used by both the standalone page and the
 * workspace's full-screen dialog (`FullscreenNavProvider`). The
 * standalone page still exists for direct-link sharing, but the
 * workspace is the primary surface — opening from the user menu
 * mounts this view inside a dialog so the canvas stays alive
 * behind it.
 *
 * Compared to the bare /local page this adds:
 *  - A prominent "Install web app" card up top that fires the native
 *    `beforeinstallprompt` event on supported platforms (Android
 *    Chrome, Chromium desktop) or shows platform-specific Add-to-
 *    Home-Screen instructions on iOS / Safari.
 *  - Friendlier "Coming soon" copy for the mobile platforms with a
 *    direct "Install web app" CTA so users aren't blocked while
 *    native builds are still in flight.
 *
 * Three fixes from the portal visual pass (2026-09-28, final-portal-ui):
 *  - every download slot shows a neutral placeholder until
 *    `/api/downloads` answers. It rendered the "no build" state first, so
 *    each card read "Coming soon" and then turned into a Download button;
 *  - `agentOnly` is the "create a local agent" view (`/local?context=agent`,
 *    titled "Run a local agent" by the workspace dialog): the SDK steps
 *    and nothing about installing the app. That link used to land on the
 *    generic view, titled "Install app";
 *  - the SDK is spelled "WebAgents" everywhere on the page, and the page's
 *    remaining English lives in en.json.
 */

interface PlatformDownload {
  url: string;
  filename: string;
  size: number;
}

interface DownloadsData {
  version?: string;
  platforms: {
    macos?: PlatformDownload;
    windows?: PlatformDownload;
    windowsMsi?: PlatformDownload;
    linux?: PlatformDownload;
    linuxDeb?: PlatformDownload;
    browserExtension?: PlatformDownload;
  };
}

function formatSize(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${(bytes / 1_000).toFixed(0)} KB`;
}

/** A download slot while `/api/downloads` is still in flight: no verdict yet. */
function PendingPill() {
  return (
    <span
      aria-hidden="true"
      data-testid="download-pending"
      className="h-7 w-24 shrink-0 rounded-full bg-muted animate-pulse"
    />
  );
}

function DownloadBadge({
  download,
  label,
  loading = false,
}: {
  download?: PlatformDownload;
  label?: string;
  loading?: boolean;
}) {
  const t = useT();
  if (loading) return <PendingPill />;
  if (!download) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full shrink-0">
        <Clock className="h-3 w-3" /> {t('settings.comingSoon')}
      </span>
    );
  }
  return (
    <a
      href={download.url}
      className="flex items-center gap-1 text-xs text-primary-foreground bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-full shrink-0 transition-colors"
    >
      <Download className="h-3 w-3" />
      {label ?? t('local.download')}
      {download.size > 0 && (
        <span className="text-primary-foreground/70">
          ({formatSize(download.size)})
        </span>
      )}
    </a>
  );
}

/**
 * Pill CTA used inside the mobile/native cards. Renders an active
 * "Install web app" button whenever the browser can either fire the
 * native prompt or guide the user through iOS Safari's Add-to-Home-
 * Screen sheet — and falls back to a quiet "Coming soon" pill
 * otherwise. Keeps the affordance present (instead of greyed out)
 * so users aren't blocked while native builds are still landing.
 */
function InstallPill({
  hint,
  applicablePlatforms,
}: {
  hint: string;
  applicablePlatforms: PwaPlatform[];
}) {
  const t = useT();
  const { canInstall, isInstalled, platform, install } = usePwaInstall();
  const isApplicable = applicablePlatforms.includes(platform);
  const iosCanGuide = platform === 'ios' && !isInstalled;
  const shouldOffer = !isInstalled && (canInstall || iosCanGuide || isApplicable);

  if (isInstalled) {
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950 px-2.5 py-1 rounded-full shrink-0">
        {t('local.installed')}
      </span>
    );
  }

  if (!shouldOffer) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full shrink-0">
        <Clock className="h-3 w-3" /> {t('settings.comingSoon')}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={hint}
      onClick={async () => {
        if (canInstall) {
          await install();
          return;
        }
        if (platform === 'ios') {
          alert(
            t('local.iosInstallHint'),
          );
        } else {
          alert(
            t('local.desktopInstallHint'),
          );
        }
      }}
      className="flex items-center gap-1 text-xs text-primary-foreground bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-full shrink-0 transition-colors"
    >
      <Download className="h-3 w-3" />
      {t('local.install')}
    </button>
  );
}

export function LocalView({
  embedded = false,
  desktopOnly = false,
  agentOnly = false,
}: {
  embedded?: boolean;
  /**
   * Compact "just the desktop apps" variant. Used when the dialog is
   * opened from a context where the only relevant install path is the
   * Robutler desktop daemon (e.g. the workspace terminal widget's
   * "Install Robutler" CTA). Hides the Install-web-app CTA, browser
   * extension card, mobile section, and developer-docs section so the
   * user is left with a single decision: which OS am I on?
   */
  desktopOnly?: boolean;
  /**
   * The "create a local agent" variant (`/local?context=agent`): how to run
   * your own agent with the WebAgents SDK and connect it, first and alone.
   * The install sections (web app, desktop, browser, mobile) belong to the
   * "Install app" entry, not to someone who asked how to make an agent.
   */
  agentOnly?: boolean;
}) {
  const t = useT();
  const [downloads, setDownloads] = useState<DownloadsData | null>(null);
  const { canInstall, isInstalled, platform, install } = usePwaInstall();

  useEffect(() => {
    // The agent view offers no downloads, so it does not ask for them.
    if (agentOnly) return;
    fetch('/api/downloads')
      .then((r) => r.json())
      .then((data: DownloadsData) => setDownloads(data))
      .catch(() => setDownloads({ platforms: {} }));
  }, [agentOnly]);

  // Until the answer lands no slot claims anything: not "Coming soon", not a
  // dimmed card. Both used to flash before the Download buttons appeared.
  const loading = downloads === null;
  const v = downloads?.version;
  const p = downloads?.platforms ?? {};

  const developerCard = (
    <div className={cn('card p-4', agentOnly ? 'mb-4' : 'mt-6')} data-testid="local-developers">
      <h3 className="text-sm font-medium mb-2">
        {t('local.forDevelopers')}
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        {t('local.forDevelopersDesc')}
      </p>
      {/* The two commands that put an agent you run yourself on
          Robutler, the same ones the CLI quickstart ends with. The page
          used to promise local agents and say nothing about how one gets
          connected (e2e report, handoff 2026-09-26). */}
      <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3" data-testid="local-run-your-own">
        <p className="text-xs font-medium mb-1">{t('local.runYourOwn')}</p>
        <p className="text-xs text-muted-foreground mb-2">{t('local.runYourOwnDesc')}</p>
        <pre className="rounded-md bg-background px-2.5 py-2 font-mono text-[11px] leading-relaxed text-foreground">
          {'webagents login\nwebagents publish'}
        </pre>
        <p className="text-xs text-muted-foreground mt-2">{t('local.runYourOwnAfter')}</p>
        <Link
          href="/develop/webagents/cli/quickstart"
          className="mt-2 text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          {t('local.quickstart')} <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      {/*
        Single link out to the developer hub. Python / TypeScript
        SDK GitHub repos used to live here but were redundant with
        what `/develop` already covers, and three links made the
        section look like a hub. One primary doorway, no chaff.
      */}
      <Link
        href="/develop"
        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
      >
        {t('local.documentation')} <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  );

  if (agentOnly) {
    return (
      <div className="max-w-lg mx-auto">
        {!embedded && (
          <div className="flex items-center gap-3 mb-6">
            <h1 className="text-lg font-semibold">{t('local.title')}</h1>
          </div>
        )}
        <div className="card p-6 mb-4">
          <p className="text-sm text-muted-foreground">{t('local.description')}</p>
        </div>
        {developerCard}
      </div>
    );
  }

  return (
    <div className={cn('max-w-lg', embedded ? 'mx-auto' : 'mx-auto')}>
      {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <div>
            <h1 className="text-lg font-semibold">{t('local.title')}</h1>
            {v && <p className="text-xs text-muted-foreground">{v}</p>}
          </div>
        </div>
      )}

      {embedded && v && (
        <p className="text-xs text-muted-foreground mb-4">{v}</p>
      )}

      <div className="card p-6 mb-4">
        <p className="text-sm text-muted-foreground">{t('local.description')}</p>
      </div>

      {/* Install web app CTA — surfaced first because most users get
          here from the user-menu "Install app" entry and we don't
          want to bury the action under download tables.
          Suppressed in `desktopOnly` mode: the dialog is intentionally
          narrowed to "pick an OS, install the daemon". */}
      {!desktopOnly && !isInstalled && (
        <button
          type="button"
          onClick={async () => {
            if (canInstall) {
              await install();
              return;
            }
            if (platform === 'ios') {
              alert(
                t('local.iosInstallHint'),
              );
            } else if (platform === 'macos') {
              alert(
                t('local.macosInstallHint'),
              );
            } else {
              alert(
                t('local.noInstallOption'),
              );
            }
          }}
          className="w-full mb-6 group rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 text-left transition-colors hover:bg-primary/10 dark:from-primary/15 dark:via-primary/8"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-sm">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-foreground">
                {t('local.installWebApp')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {canInstall
                  ? t('local.oneTapInstall')
                  : platform === 'ios'
                    ? t('local.iosInstallShort')
                    : platform === 'macos'
                      ? t('local.macosInstallShort')
                      : t('local.nativeAppsComing')}
              </p>
            </div>
            <span className="text-xs font-medium text-primary group-hover:underline shrink-0 self-center">
              {t('local.install')}
            </span>
          </div>
        </button>
      )}

      <div className="space-y-2">
        <h2 className="text-sm font-medium text-muted-foreground px-1">
          {t('local.desktop')}
        </h2>

        <div
          className={cn(
            'card p-4 flex items-center gap-4',
            !loading && !p.macos ? 'opacity-90' : '',
          )}
        >
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Apple className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm">{t('local.macOS')}</p>
            <p className="text-xs text-muted-foreground">
              {t('local.macOSDesc')}
            </p>
          </div>
          {loading ? (
            <PendingPill />
          ) : p.macos ? (
            <DownloadBadge download={p.macos} />
          ) : (
            <InstallPill
              hint={t('local.installWebAppHint')}
              applicablePlatforms={['macos']}
            />
          )}
        </div>

        <div
          className={cn(
            'card p-4 flex items-center gap-4',
            !loading && !p.windows ? 'opacity-60' : '',
          )}
        >
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Monitor className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm">{t('local.windows')}</p>
            <p className="text-xs text-muted-foreground">
              {t('local.windowsDesc')}
            </p>
          </div>
          <DownloadBadge download={p.windows} loading={loading} />
        </div>

        <div
          className={cn(
            'card p-4 flex items-center gap-4',
            !loading && !p.linux && !p.linuxDeb ? 'opacity-60' : '',
          )}
        >
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Monitor className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm">{t('local.linux')}</p>
            <p className="text-xs text-muted-foreground">
              {t('local.linuxDesc')}
            </p>
          </div>
          <DownloadBadge download={p.linux ?? p.linuxDeb} loading={loading} />
        </div>
      </div>

      {!desktopOnly && (
        <div className="space-y-2 mt-6">
          <h2 className="text-sm font-medium text-muted-foreground px-1">
            {t('local.browser')}
          </h2>

          <div
            className={cn(
              'card p-4 flex items-center gap-4',
              !loading && !p.browserExtension ? 'opacity-60' : '',
            )}
          >
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Chrome className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm">{t('local.chromeExtension')}</p>
              <p className="text-xs text-muted-foreground">
                {t('local.chromeExtensionDesc')}
              </p>
            </div>
            <DownloadBadge download={p.browserExtension} label={t('local.install')} loading={loading} />
          </div>
        </div>
      )}

      {/* Mobile: native iOS / Android builds are pending. The Install
          web app card up top is the path for mobile users today
          (Safari → Add to Home Screen on iOS, Chrome's native
          install prompt on Android), so these cards stay disabled
          with a "Coming soon" pill until the native apps ship —
          avoids the impression that we have two parallel install
          flows on the same platform. */}
      {!desktopOnly && (
        <div className="space-y-2 mt-6">
          <h2 className="text-sm font-medium text-muted-foreground px-1">
            {t('local.mobile')}
          </h2>

          <div className="card p-4 flex items-center gap-4 opacity-60">
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Smartphone className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm">{t('local.iOS')}</p>
              <p className="text-xs text-muted-foreground">{t('local.iOSDesc')}</p>
            </div>
            <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full shrink-0">
              <Clock className="h-3 w-3" /> {t('local.comingSoon')}
            </span>
          </div>

          <div className="card p-4 flex items-center gap-4 opacity-60">
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Smartphone className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm">{t('local.android')}</p>
              <p className="text-xs text-muted-foreground">
                {t('local.androidDesc')}
              </p>
            </div>
            <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full shrink-0">
              <Clock className="h-3 w-3" /> {t('local.comingSoon')}
            </span>
          </div>
        </div>
      )}

      {!desktopOnly && developerCard}
    </div>
  );
}
